import { MaterialCommunityIcons, MaterialIcons } from "@expo/vector-icons";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  Card,
  Divider,
  Page,
  PayTypeToIcon,
  Stack,
} from "@towersystems/roam-common-native";
import { FlatList, Pressable } from "react-native";
import { Colors, Spacings, Text, View } from "react-native-ui-lib";
import {
  CheckoutRootParamList,
  PaymentRootScreenParamList,
} from "../../../../types";
import PaymentMethod from "../../../../utilities/data/model/PaymentMethod";
import { useGlobalSettingsProvider } from "../../../../utilities/global-settings/hooks";
import { moneyToFormattedString } from "../../../../utilities/money";
import { useCheckoutContextProvider } from "../../utils/checkout";
import { CustomerInvoiceOptions } from "./components/CustomerInvoiceOptons";
import { SelectablePaymentMethodButton } from "./components/SelectablePaymentMethodButton";
import useOnline from "@towersystems/roam-common-native/lib/utilities/use-online";
import { useCancelSale } from "../../utils/sale-controller";
import React from "react";
import Sale from "../../../../utilities/data/model/Sale";
import { vendorCanAccessFeature } from "../../../../utilities/feature-flags";
import { AccountFeatureFlag } from "@towersystems/roam-common/lib/generated-types";
import { useCurrentVendorProvider } from "../../../../utilities/vendor";

// page doesnt have access to data currently
// replace once can access data
export const onlineOnlyIds = ["10", "15"];

export type SelectPaymentMethodScreenProps = NativeStackScreenProps<
  CheckoutRootParamList & PaymentRootScreenParamList,
  "SelectPaymentMethodScreen"
>;

export const SelectPaymentMethodScreen = ({
  navigation,
}: SelectPaymentMethodScreenProps) => {
  const globalSettings = useGlobalSettingsProvider();
  const cancelSale = useCancelSale();
  const { currentVendor } = useCurrentVendorProvider();
  const online = useOnline();

  const { sale, selectablePaymentMethods, balance } =
    useCheckoutContextProvider();

  const handleOnCancel = React.useCallback(async () => {
    cancelSale().then(() => navigation.pop());
  }, [cancelSale]);

  const renderItem = ({ item }: { item: PaymentMethod }) => {
    return (
      <SelectablePaymentMethodButton
        onPress={() => {
          navigation.navigate(
            "PaymentRootScreen",
            //@ts-ignore
            {
              screen: payTypeToPaymentScreen(item.payType.id),
              paymentMethodId: item.id,
              tenderedAmount: balance,
              minTenderAmount: balance,
            }
          );
        }}
        label={item.name}
        icon={<PayTypeToIcon payType={item.payType.id} />}
        disabled={
          // disable online only payment methods if offline
          // of if zero and not cash
          (onlineOnlyIds.includes(item.payType.id) && !online) ||
          (balance == 0 && item.payType.id !== "1")
        }
      />
    );
  };

  const secondaryTitle =
    sale.payments.length >= 1 ? (
      <Pressable
        onPress={() => navigation.navigate("CheckoutPaymentHistoryScreen")}
      >
        <Text style={{ color: Colors.action, fontSize: 22 }}>{`${
          sale.payments.length
        } payments (${moneyToFormattedString(sale.paymentsTotal)})`}</Text>
      </Pressable>
    ) : undefined;

  const isLayby = sale.saleState.includes("LAYBY");

  const customerInvoiceMarkup = vendorCanAccessFeature(
    AccountFeatureFlag.POS_APP_INVOICES_UI,
    currentVendor
  ) && (
    <CustomerInvoiceOptions
      sale={sale}
      onPress={() => {
        navigation.navigate("CheckoutInvoiceRootScreen", {
          screen: "CheckoutInvoiceScreen",
        });
      }}
    />
  );

  const selectablePaymentMethodsMarkup =
    sale.isSale || sale.isProforma ? (
      <Card style={{ padding: 0 }}>
        <FlatList
          ItemSeparatorComponent={() => <Divider marginVertical={0} />}
          renderItem={renderItem}
          data={selectablePaymentMethods}
          scrollEnabled={false}
        ></FlatList>
      </Card>
    ) : undefined;

  const content = !isLayby ? (
    <View>
      <Card style={{ marginTop: Spacings.s8, padding: 0 }}>
        <SelectablePaymentMethodButton
          disabled={balance == 0 || false == sale.isSale}
          onPress={() => navigation.navigate("SplitPaymentScreen")}
          label="Split Payment"
          description="Split payment between multiple payment methods"
          icon={
            <MaterialCommunityIcons
              name="call-split"
              color={Colors.text}
              size={26}
            />
          }
        />
        {vendorCanAccessFeature(
          AccountFeatureFlag.POS_APP_QUOTES_UI,
          currentVendor
        ) && (
          <>
            <Divider marginVertical={0} />
            <SelectablePaymentMethodButton
              disabled={
                sale.isQuote
                  ? false
                  : !sale.isSale || balance == 0 || !sale.customer.id
              }
              onPress={() =>
                navigation.navigate("CreateSaleQuoteRootScreen", {
                  screen: "CompleteQuoteScreen",
                })
              }
              label="Quote"
              description="Convert sale to quote"
              icon={
                <MaterialIcons
                  name="request-quote"
                  size={26}
                  color={Colors.text}
                />
              }
            />
          </>
        )}
        {vendorCanAccessFeature(
          AccountFeatureFlag.POS_LAYBY_UI,
          currentVendor
        ) &&
          (globalSettings.laybyEnabled ? (
            <>
              <Divider marginVertical={0} />
              <SelectablePaymentMethodButton
                onPress={() =>
                  navigation.navigate("CheckoutLaybyRootScreen", {
                    screen: "CheckoutLaybyScreen",
                  })
                }
                label="Layby"
                description={`Start a layby for existing customer. Min spend ${moneyToFormattedString(
                  globalSettings.minLaybySpend
                )}.`}
                disabled={
                  !sale.customer.id ||
                  balance < globalSettings.minLaybySpend ||
                  sale.isQuote ||
                  sale.isInvoice
                }
                icon={
                  <MaterialCommunityIcons
                    name="credit-card-clock-outline"
                    color={Colors.text}
                    size={26}
                  />
                }
              />
            </>
          ) : null)}

        {customerInvoiceMarkup ? customerInvoiceMarkup : null}
      </Card>
    </View>
  ) : (
    <View>
      <Card style={{ marginTop: Spacings.s8, padding: 0 }}>
        <SelectablePaymentMethodButton
          onPress={() =>
            navigation.navigate("CheckoutLaybyRootScreen", {
              screen: "CheckoutLaybyScreen",
            })
          }
          label="Layby"
          description={`Continue layby. Min spend ${moneyToFormattedString(
            globalSettings.minLaybySpend
          )}.`}
          disabled={!sale.customer.id || balance < globalSettings.minLaybySpend}
          icon={
            <MaterialCommunityIcons
              name="credit-card-clock-outline"
              color={Colors.text}
              size={26}
            />
          }
        />
        <Divider marginVertical={0} />
        <SelectablePaymentMethodButton
          onPress={handleOnCancel}
          label="Cancel Layby"
          description={`Cancel layby.`}
          icon={
            <MaterialIcons name="cancel" color={Colors.destructive} size={26} />
          }
        />
      </Card>
    </View>
  );

  const title = saleToTitle(sale);

  return (
    <Page
      titleContainerStyle={{ alignItems: "center" }}
      title={title}
      secondaryTitle={secondaryTitle}
    >
      <Stack vertical>
        {selectablePaymentMethodsMarkup}
        {content}
      </Stack>
    </Page>
  );
};

const saleToTitle = (sale: Sale) => {
  if (sale.saleState.includes("LAYBY")) {
    return "Continue Layby";
  }

  if (sale.isQuote) {
    return "Complete Quote";
  }

  if (sale.isInvoice) {
    return "Complete Invoice";
  }

  return "Select Payment Method";
};

const payTypeToPaymentScreen = (payTypeId: string) => {
  switch (payTypeId) {
    case "35":
      return "GiftCardPaymentRootScreen";
    default:
      return "PaymentScreen";
  }
};
